<template>
    <div class="d-flex flex-column">

        <!-- Mounted once here, not inside the account menu: that menu's content
             is destroyed when it closes, and the checklist toolbar opens the same
             dialog from a different screen. -->
        <DialogSelectEmployees />

        <div v-if="rail" class="mt-12">
            <v-list color="accent" nav slim :density="nav_density" class="pit-nav">
                <ButtonUser listitem />
                <v-list-item :disabled="!m_prod_management" @click="openQrReader">
                    <template v-slot:prepend>
                        <v-icon> mdi-qrcode-scan </v-icon>
                    </template>
                    <v-list-item-title>
                        qr
                    </v-list-item-title>
                </v-list-item>
                <ButtonLanguage listitem v-if="user.auth.multilang" />
                <ButtonNotifications listitem />
            </v-list>
        </div>

        <v-sheet v-else class="sticky-header">
            <!-- Centred on the logo's box, not on the image: a wide, low logo
                 used to sit at the top of the row with the buttons below it. -->
            <div class="d-flex align-center justify-space-around ml-10 mb-3">
                <ButtonLanguage v-if="user.auth.multilang" icon />
                <div class="logo-container">
                    <v-img v-if="company_logo" transition="scale-transition" max-width="130" max-height="50" contain
                        :class="dark ? 'inverted' : ''" :src="company_logo"></v-img>
                    <v-img v-else-if="dark" transition="scale-transition" max-width="130" contain
                        src="https://storage.googleapis.com/produuzit-public/img/pit_logo_dark.svg"></v-img>
                    <v-img v-else transition="scale-transition" max-width="130" contain
                        src="https://storage.googleapis.com/produuzit-public/img/pit_logo.svg">
                    </v-img>
                </div>
                <v-btn @click="openQrReader" icon="mdi-qrcode-scan" class="ml-2" variant="text">
                </v-btn>
                <ButtonNotifications />
            </div>


            <div class="d-flex flex-row top-row">
                <div>
                    <ButtonUser />
                </div>
                <!-- A worker at a company working by date never picks a
                     project: their list is the day's, and the control that
                     decides it lives above the list itself (the viewer header,
                     the logistics tree). Rendering one here too put the same
                     date selector on screen twice, and left it on screen doing
                     nothing once a project was reached by QR. Nothing takes the
                     slot, so the account button spans the row. -->
                <div class="ml-1" v-if="!by_date_worker">
                    <SelectorProject />
                </div>
            </div>
            <!-- One toggle for every role. It renders the modes this
                 account may enter and hides itself when there is only one, so
                 there is no second component for production workers. -->
            <div class="d-flex flex-row">
                <ButtonMode />
            </div>
            <MenuHeaderViewerButtons v-if="viewer_mode && !rail" />
            <FilterRackLoads v-if="delivery_mode && !rail" />

        </v-sheet>

        <div v-if="viewer_mode && !rail">
            <ListViewerGroup />
        </div>

        <div v-if="delivery_mode && !rail">
            <ListRackLoads />
        </div>

        <div v-if="company_wide && manager_menu">
            <v-list color="accent" nav slim :density="nav_density" class="pit-nav" v-model:opened="opened_groups">

                <v-list-item :to="'/company/dashboard'">
                    <template v-slot:prepend>
                        <v-icon> mdi-view-dashboard </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("dashboard") }}
                        <!-- <v-chip size="small" class="ml-2" variant="outlined" color="accent"> beta
                        </v-chip> -->
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :disabled="!m_prod_management" :to="'/company/production_overview'">
                    <template v-slot:prepend>
                        <v-icon> mdi-chart-gantt </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("production_overview") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :to="'/company/issues'">
                    <template v-slot:prepend>
                        <v-icon> mdi-garage-alert-variant </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("production_issues") }}
                    </v-list-item-title>
                </v-list-item>

                <v-divider class="my-2" />
                <v-list-subheader>{{ t("settings") }}</v-list-subheader>

                <v-list-item :to="'/company/settings'" @click="selectCompanyWide">
                    <template v-slot:prepend>
                        <v-icon> mdi-cog </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("company_settings") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :to="'/company/projects'" @click="selectCompanyWide">
                    <template v-slot:prepend>
                        <v-icon> mdi-office-building-cog-outline </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("projects_settings") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-group value="production_settings">
                    <template v-slot:activator="{ props }">
                        <v-list-item v-bind="props">
                            <template v-slot:prepend>
                                <v-icon> mdi-window-shutter-cog </v-icon>
                            </template>
                            <v-list-item-title>
                                {{ t("production_settings") }}
                            </v-list-item-title>
                        </v-list-item>
                    </template>

                    <v-list-item :to="'/company/production_lines'" @click="selectCompanyWide">
                        <template v-slot:prepend>
                            <v-icon> mdi-transit-connection-variant </v-icon>
                        </template>
                        <v-list-item-title>
                            {{ t("production_lines") }}
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item :to="'/company/machines'" @click="selectCompanyWide">
                        <template v-slot:prepend>
                            <v-icon> mdi-robot-industrial-outline </v-icon>
                        </template>
                        <v-list-item-title>
                            {{ t("machine_settings") }}
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item :to="'/company/automations'" @click="selectCompanyWide">
                        <template v-slot:prepend>
                            <v-icon> mdi-robot-outline </v-icon>
                        </template>
                        <v-list-item-title>
                            {{ t("automations") }}
                        </v-list-item-title>
                    </v-list-item>
                </v-list-group>

                <v-divider class="my-2" />
                <v-list-subheader>{{ t("files") }}</v-list-subheader>

                <v-list-item :disabled="!m_bom && !m_machines" :to="'/company/production_files'">
                    <template v-slot:prepend>
                        <v-icon> mdi-saw-blade </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("production_files") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :disabled="!m_bom" :to="'/company/management_files'">
                    <template v-slot:prepend>
                        <v-icon> mdi-file-document-outline </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("management_files") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :disabled="!m_lite" :to="'/company/tools/moblite'">
                    <template v-slot:prepend>
                        <v-icon> mdi-file-cog </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("mobfile_generator") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :to="'/company/file_settings'" @click="selectCompanyWide">
                    <template v-slot:prepend>
                        <v-icon> mdi-file-settings-outline </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("file_settings") }}
                    </v-list-item-title>
                </v-list-item>

                <v-divider class="my-2" />
                <v-list-subheader>{{ t("constructibles") }}</v-list-subheader>

                <v-list-item :to="'/company/constructibles_manager'">
                    <template v-slot:prepend>
                        <v-icon> mdi-garage-variant </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("constructibles_list") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :to="'/company/parts'">
                    <template v-slot:prepend>
                        <v-icon> mdi-lightbulb-fluorescent-tube-outline </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("parts_list") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :to="'/company/part_batches'">
                    <template v-slot:prepend>
                        <v-icon> mdi-tray </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("part_batches") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :to="'/company/sizes_and_materials'">
                    <template v-slot:prepend>
                        <v-icon> mdi-tape-measure </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("sizes_and_materials") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="m_delivery" :to="'/company/rack_setups'">
                    <template v-slot:prepend>
                        <v-icon> mdi-bookshelf </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("rack_setups") }}
                    </v-list-item-title>
                </v-list-item>

            </v-list>
        </div>

        <div v-if="logistics_mode && !rail">
            <v-container>
                <v-row dense>
                    <v-col>
                        <SelectTreeLogisticsByDate v-if="work_by_date && company_wide" />
                        <SelectTreeLogistics v-else />
                    </v-col>
                </v-row>
            </v-container>
        </div>
        <div v-if="!company_wide && manager_menu">
            <v-list color="accent" nav slim :density="nav_density" class="pit-nav">
                <!-- <v-list-item v-if="!stdlib_mode" disabled :to="'/projects/' + selected_project_uuid + '/overview'">
                    <template v-slot:prepend>
                        <v-icon> mdi-chart-gantt </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("project_overview") }}
                    </v-list-item-title>
                </v-list-item> -->

                <v-list-subheader>{{ t("designs") }}</v-list-subheader>

                <!-- Work waiting on a page shows on its entry, from any page of the
                     project: a pill, a dot, a spinner, a lock or an alert at the
                     end, and a dot on the icon when the menu is a rail
                     (docs/update-designs-proposal.md §5.7). -->
                <v-list-item :to="'/projects/' + selected_project_uuid + '/files'">
                    <template v-slot:prepend>
                        <v-badge :model-value="rail && files_indicator.kind != 'none'" dot
                            :color="files_indicator.color">
                            <v-icon> mdi-file-cog-outline </v-icon>
                        </v-badge>
                    </template>
                    <v-list-item-title>
                        {{ t("files") }}
                    </v-list-item-title>
                    <template v-if="!rail" v-slot:append>
                        <MenuIndicator :indicator="files_indicator" :tooltip="files_tooltip" />
                    </template>
                </v-list-item>

                <v-list-item :to="'/projects/' + selected_project_uuid + '/designs'">
                    <template v-slot:prepend>
                        <v-icon> mdi-text-box-multiple-outline </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("designs") }}
                    </v-list-item-title>
                </v-list-item>

                <v-divider class="my-2" />
                <v-list-subheader>{{ t("constructibles") }}</v-list-subheader>

                <v-list-item v-if="!stdlib_mode" :to="'/projects/' + selected_project_uuid + '/quantities'">
                    <template v-slot:prepend>
                        <v-badge :model-value="rail && !['none', 'lock'].includes(counts_indicator.kind)" dot
                            :color="counts_indicator.color">
                            <v-icon> mdi-dots-grid </v-icon>
                        </v-badge>
                    </template>
                    <v-list-item-title>
                        {{ t("production_counts") }}
                    </v-list-item-title>
                    <template v-if="!rail" v-slot:append>
                        <MenuIndicator :indicator="counts_indicator" :tooltip="counts_tooltip" />
                    </template>
                </v-list-item>

                <v-list-item v-if="!stdlib_mode" :to="'/projects/' + selected_project_uuid + '/constructibles'">
                    <template v-slot:prepend>
                        <v-icon> mdi-garage-variant </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("constructibles_list") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="!stdlib_mode" :disabled="!m_multipanel"
                    :to="'/projects/' + selected_project_uuid + '/multipanel'">
                    <template v-slot:prepend>
                        <v-icon>mdi-set-merge</v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("multipanel_groups") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :to="'/projects/' + selected_project_uuid + '/parts'">
                    <template v-slot:prepend>
                        <v-icon> mdi-lightbulb-fluorescent-tube-outline </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("parts_list") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :to="'/projects/' + selected_project_uuid + '/part_batches'">
                    <template v-slot:prepend>
                        <v-icon> mdi-tray </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("part_batches") }}
                    </v-list-item-title>
                </v-list-item>

                <v-divider class="my-2" />
                <v-list-subheader>{{ t("files") }}</v-list-subheader>

                <v-list-item :disabled="!m_bom && !m_machines"
                    :to="'/projects/' + selected_project_uuid + '/production_files'">
                    <template v-slot:prepend>
                        <v-icon> mdi-saw-blade </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("production_files") }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item :disabled="!m_bom" :to="'/projects/' + selected_project_uuid + '/management_files'">
                    <template v-slot:prepend>
                        <v-icon> mdi-file-document-outline </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("management_files") }}
                    </v-list-item-title>
                </v-list-item>

                <template v-if="!stdlib_mode">
                    <v-divider class="my-2" />
                    <v-list-subheader>{{ t("production") }}</v-list-subheader>

                    <v-list-item :to="'/projects/' + selected_project_uuid + '/issues'">
                        <template v-slot:prepend>
                            <v-icon> mdi-garage-alert-variant </v-icon>
                        </template>
                        <v-list-item-title>
                            {{ t("production_issues") }}
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item :disabled="!m_delivery" :to="'/projects/' + selected_project_uuid + '/delivery'">
                        <template v-slot:prepend>
                            <v-icon> mdi-truck-delivery-outline </v-icon>
                        </template>
                        <v-list-item-title>
                            {{ t("delivery") }}
                        </v-list-item-title>
                    </v-list-item>
                </template>

                <v-divider class="my-2" />
                <v-list-subheader>{{ t("settings") }}</v-list-subheader>


                <!-- Opens the same dialog as the pencil in the company's projects table. -->
                <v-list-item @click="project_dialog = true">
                    <template v-slot:prepend>
                        <v-icon> mdi-folder-cog-outline </v-icon>
                    </template>
                    <v-list-item-title>
                        {{ t("project_settings") }}
                    </v-list-item-title>
                </v-list-item>
                <DialogProjectForm v-model="project_dialog" :project_uuid="selected_project_uuid"
                    :standard_library="stdlib_mode" />
            </v-list>
        </div>
        <div class="pa-10">

        </div>
        <div v-if="company_logo && !rail" class="logo-container-bottom white-gradient-color d-flex"
            :class="dark ? 'dark' : ''">
            <v-img v-if="dark" transition="scale-transition" max-width="130" contain class="mx-auto mt-5 dark"
                src="https://storage.googleapis.com/produuzit-public/img/pit_logo_dark.svg"></v-img>
            <v-img v-else transition="scale-transition" max-width="130" contain class="mx-auto mt-5"
                src="https://storage.googleapis.com/produuzit-public/img/pit_logo.svg"></v-img>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 16px;
    padding-bottom: 8px;
}

// The user button and the project/date selector share the row 50/50 and
// fill the drawer width (at any size), so their edges align with the
// mode toggle below.
.top-row>div {
    flex: 1 1 0;
    min-width: 0;
}



.dark {
    -webkit-filter: invert(1);
    filter: invert(1);
}

.inverted {
    filter: invert(64%) contrast(228%) brightness(80%) hue-rotate(180deg);
}

// A fixed box, so the row has the same height whatever the logo's shape,
// with the logo centred in it both ways.
.logo-container {
    width: 130px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.white-background {
    background-color: lightslategrey;
}

.logo-container-bottom {
    position: fixed;
    left: 0;
    bottom: 0;
    height: 80px;
    width: 100%;
    // never wider than the drawer that hosts it
    max-width: 400px;
    text-align: center;
}



.white-gradient-color {
    background: rgb(255, 255, 255);
    background: linear-gradient(0deg,
            rgba(255, 255, 255, 1) 30%,
            rgba(255, 255, 255, 0) 100%);
}
</style>

<script>
import ButtonLanguage from "/src/components/account/ButtonLanguage";
import ButtonNotifications from "./ButtonNotifications.vue";
import ButtonUser from "./ButtonUser";
import DialogSelectEmployees from "./DialogSelectEmployees.vue";
import ListViewerGroup from "./list/ListViewerGroup.vue";
import ListRackLoads from "./list/ListRackLoads.vue";
import FilterRackLoads from "./FilterRackLoads.vue";
import MenuHeaderViewerButtons from "./list/MenuHeaderViewerButtons.vue";
import ButtonMode from "./ButtonMode";
import MenuIndicator from "./MenuIndicator.vue";
import SelectorProject from "./SelectorProject";
import SelectTreeLogistics from "./SelectTreeLogistics.vue";
import { useAppStore } from "@/stores/app";
import { useCloudstorageStore } from "@/stores/cloudstorage";
import { useCompanyStore } from "@/stores/company";
import { useSelectedStore } from "@/stores/selected";
import { useSessionStore } from "@/stores/session";
import { useTranslationsStore } from "@/stores/translations";
import { useViewerStore } from "@/stores/viewer";
import SelectTreeLogisticsByDate from "./SelectTreeLogisticsByDate.vue";
import DialogProjectForm from "@/components/company/projects/DialogProjectForm.vue";
import { useIsTouch } from "@/composables/touch";



export default {
    setup() {
        const app_store = useAppStore()
        const cloudstorage_store = useCloudstorageStore()
        const company_store = useCompanyStore()
        const selected_store = useSelectedStore()
        const session_store = useSessionStore()
        const translations_store = useTranslationsStore()
        const viewer_store = useViewerStore()

        const t = translations_store.translate

        return {
            ...useIsTouch(),
            app_store,
            cloudstorage_store,
            company_store,
            selected_store,
            session_store,
            t,
            viewer_store,
        }
    },
    components: {
        ButtonLanguage,
        ButtonNotifications,
        ButtonUser,
        DialogSelectEmployees,
        ListViewerGroup,
        ListRackLoads,
        FilterRackLoads,
        MenuHeaderViewerButtons,
        ButtonMode,
        MenuIndicator,
        SelectorProject,
        SelectTreeLogistics,
        SelectTreeLogisticsByDate,
        DialogProjectForm,
    },
    props: ["rail"],
    data: () => ({
        // Which v-list-groups are expanded. Kept in sync with the route so the
        // active child is visible after a reload or a direct link.
        opened_groups: [],
        project_dialog: false,
    }),
    watch: {
        "$route.name": {
            immediate: true,
            handler() {
                const in_group = [
                    "company_production_lines_settings",
                    "company_machine_settings",
                    "company_automations_settings",
                ].includes(this.$route.name)
                if (in_group && !this.opened_groups.includes("production_settings")) {
                    this.opened_groups = [...this.opened_groups, "production_settings"]
                }
            },
        },
        company_logo_cf: {
            immediate: true,
            deep: true,
            handler(cf) {
                this.cloudstorage_store.ensurePresignedUrls(cf ? [cf] : [])
            },
        },
        user(u) {
            if (u?.auth?.hide_panel_queue)
                this.viewer_store.setDesignsMode(true);
            else this.viewer_store.setDesignsMode(false);
        },
        manager_or_admin(moa) {
            if (!moa) {
                this.app_store.setViewerMode();
            }
        },
    },
    computed: {
        dark() {
            return this.$vuetify.theme.global.current.dark
        },
        // 40 px rows with a mouse, 48 px on a touchscreen
        // (docs/left-menu-density-proposal.md).
        nav_density() {
            return this.isTouch ? "default" : "compact"
        },
        user() {
            return this.session_store.user_object;
        },
        loading() {
            return this.selected_store.viewer_loading;
        },
        viewer_mode() {
            return this.app_store.app_mode == "production";
        },
        logistics_mode() {
            return this.app_store.app_mode == "logistics";
        },
        delivery_mode() {
            return this.app_store.app_mode == "delivery";
        },
        // The manager route list belongs to the manage mode, not to "every
        // mode that is not the viewer or logistics" — which is what it used to
        // say, and what a fourth mode would have broken.
        manager_menu() {
            return this.manager_or_admin && this.app_store.app_mode == "manage";
        },
        stdlib_mode() {
            return this.selected_store.stdlib_mode
        },
        files_indicator() {
            return this.selected_store.filesIndicator
        },
        counts_indicator() {
            return this.selected_store.countsIndicator
        },
        files_tooltip() {
            const ind = this.files_indicator
            const run = this.selected_store.designRun
            if (ind.kind == "pill") {
                const lines = []
                if (ind.pending) lines.push(this.t("menu_files_pending").replace("{n}", ind.pending))
                if (ind.needs_category) lines.push(this.t("menu_files_need_category").replace("{n}", ind.needs_category))
                return lines.join(" · ") || this.t("menu_files_pending").replace("{n}", ind.count)
            }
            if (ind.kind == "spinner") return this.t("design_update_running_short")
            if (ind.kind == "alert") return run?.stalled ? this.t("design_update_stalled_title") : this.t("design_update_failed_title")
            return ""
        },
        counts_tooltip() {
            const ind = this.counts_indicator
            if (ind.kind == "pill") return this.t("menu_counts_differ")
                .replace("{create}", ind.to_create || 0).replace("{delete}", ind.to_delete || 0)
            if (ind.kind == "dot") return this.t("stale_counts_title")
            if (ind.kind == "lock") return this.t("menu_counts_locked")
            return ""
        },
        // The same allowlist `onlyManagersGuard` uses. This used to be
        // `!iam_prod_worker`, which called every external viewer and every
        // no-rights account a manager: 52 accounts got the whole manager menu
        // and a red "no permissions" toast on every entry in it.
        manager_or_admin() {
            return this.session_store.iam_manager_or_admin;
        },
        i_am_staff() {
            return this.session_store.user_object?.auth?.pit_staff;
        },
        // A production worker at a company working by date, who has no project
        // to pick: the day is the scope. Asked directly rather than through
        // `manager_or_admin`, which excludes external viewers too.
        by_date_worker() {
            if (!this.session_store.iam_prod_worker) return false
            return this.company?.work_by_date || false
        },
        projects() {
            return this.company_store.projects;
        },
        company_wide() {
            return this.selected_project_uuid == "all";
        },
        selected_project_uuid() {
            return this.selected_store.selected_project?.uuid || "";
        },
        selected_project() {
            return this.selected_store.selected_project || "";
        },
        company() {
            return this.session_store.user_object?.auth?.company;
        },
        company_pck() {
            return this.cloudstorage_store.packagesByNameAndLevel({
                name: "resources",
                level: "company",
            })?.[0]
        },
        company_logo_cf() {
            return this.company_pck?.files?.find(cf => cf.meta.section == "logo")
        },
        company_logo() { return this.company_logo_cf?.presigned_url },
        m_prod_management() {
            return this.company?.m_prod_management || false;
        },
        m_bom() {
            return this.company?.m_bom || false;
        },
        m_machines() {
            return this.company?.m_machines || false;
        },
        m_lite() {
            return this.session_store.mobliteLicensed;
        },
        m_delivery() {
            return this.session_store.hasAnyModule(['m_delivery'])
        },
        m_multipanel() {
            return this.session_store.hasAnyModule(['m_multipanel'])
        },
        m_logistics() {
            return this.session_store.hasAnyModule(['m_logistics'])
        },
        work_by_date() {
            return this.company?.work_by_date
        },
    },
    methods: {
        openQrReader() {
            this.app_store.qr_reader_open = true
        },
        // Navigation is left to :to on the item so Vuetify can mark it active;
        // VListItem emits click before it navigates, so this lands first.
        selectCompanyWide() {
            this.selected_store.setCompanyWide()
            this.selected_store.setProjectSelection("all")
        },
    }
};
</script>
